const getCutoffTime = (range) => {
  const now = Date.now()
  switch (range) {
    case '6h': return now - 6 * 60 * 60 * 1000
    case '12h': return now - 12 * 60 * 60 * 1000
    case '1d': return now - 24 * 60 * 60 * 1000
    case '3d': return now - 3 * 24 * 60 * 60 * 1000
    case '7d': return now - 7 * 24 * 60 * 60 * 1000
    case '14d': return now - 14 * 24 * 60 * 60 * 1000
    case '1m': return now - 30 * 24 * 60 * 60 * 1000
    case '2m': return now - 60 * 24 * 60 * 60 * 1000
    case '6m': return now - 182 * 24 * 60 * 60 * 1000
    case '1y': return now - 365 * 24 * 60 * 60 * 1000
    default: return now - 6 * 60 * 60 * 1000
  }
}

const getTimeUnit = (range) => {
  if (['6h', '12h', '1d', '3d'].includes(range)) return 'hour'
  if (['7d', '14d', '1m', '2m'].includes(range)) return 'day'
  if (['6m', '1y'].includes(range)) return 'month'
  return 'hour'
}

const pad = (n) => String(n).padStart(2, '0')

const getBucketKey = (date, unit) => {
  const d = new Date(date)
  const year = d.getFullYear()
  const month = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const hour = pad(d.getHours())

  switch (unit) {
    case 'hour':
      return `${year}-${month}-${day} ${hour}:00`
    case 'day':
      return `${year}-${month}-${day}`
    case 'month':
      return `${year}-${month}`
    default:
      return `${year}-${month}-${day} ${hour}:00`
  }
}

const formatLabel = (bucketKey, unit) => {
  const parts = bucketKey.split(/[\s:-]/)

  if (unit === 'hour') {
    const hour = parts[3]
    return `${hour}:00`
  }

  if (unit === 'day') {
    const [year, month, day] = parts
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  if (unit === 'month') {
    const [year, month] = parts
    const date = new Date(year, month - 1)
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  return bucketKey
}

const generateAllBuckets = (cutoff, now, unit) => {
  const buckets = []
  let d = new Date(cutoff)
  const end = new Date(now)

  // Normalize to start of period
  if (unit === 'hour') {
    d.setMinutes(0, 0, 0)
  } else if (unit === 'day') {
    d.setHours(0, 0, 0, 0)
  } else if (unit === 'month') {
    d.setDate(1)
    d.setHours(0, 0, 0, 0)
  }

  // Skip the first bucket by advancing once
  switch (unit) {
    case 'hour': d.setHours(d.getHours() + 1); break
    case 'day': d.setDate(d.getDate() + 1); break
    case 'month': d.setMonth(d.getMonth() + 1); break
    default: d.setHours(d.getHours() + 1); break
  }

  while (d <= end) {
    buckets.push(getBucketKey(d, unit))
    switch (unit) {
      case 'hour': d.setHours(d.getHours() + 1); break
      case 'day': d.setDate(d.getDate() + 1); break
      case 'month': d.setMonth(d.getMonth() + 1); break
      default: d.setHours(d.getHours() + 1); break
    }
  }

  return buckets
}

const aggregateCounts = (arr, unit) => {
  const counts = {}
  arr.forEach(item => {
    if (!item?.createdAt) return
    const key = getBucketKey(item.createdAt, unit)
    counts[key] = (counts[key] || 0) + 1
  })
  return counts
}

const aggregateRevenue = (arr, unit, field = 'amount') => {
  const sums = {}
  arr.forEach(item => {
    if (!item?.createdAt) return
    const key = getBucketKey(item.createdAt, unit)
    sums[key] = (sums[key] || 0) + (item[field] || 0)
  })
  return sums
}

export const getStats = (test, range = '6h', stat = 'views') => {
  const stats_a = test?.stat_test_a?.advancedStats?.[stat] || []
  const stats_b = test?.stat_test_b?.advancedStats?.[stat] || []

  const now = Date.now()
  const cutoff = getCutoffTime(range)
  const unit = getTimeUnit(range)

  // For revenue_per_view, we need cumulative data for each variant separately
  if (stat === 'revenue_per_view') {
    const revenues_a = test?.stat_test_a?.advancedStats?.revenues || []
    const revenues_b = test?.stat_test_b?.advancedStats?.revenues || []
    const views_a = test?.stat_test_a?.advancedStats?.views || []
    const views_b = test?.stat_test_b?.advancedStats?.views || []

    // Filter
    const filtered_rev_a = revenues_a.filter(v => v.createdAt >= cutoff)
    const filtered_rev_b = revenues_b.filter(v => v.createdAt >= cutoff)
    const filtered_views_a = views_a.filter(v => v.createdAt >= cutoff)
    const filtered_views_b = views_b.filter(v => v.createdAt >= cutoff)

    // Aggregate
    const agg_rev_a = aggregateRevenue(filtered_rev_a, unit, 'amount')
    const agg_rev_b = aggregateRevenue(filtered_rev_b, unit, 'amount')
    const agg_views_a = aggregateCounts(filtered_views_a, unit)
    const agg_views_b = aggregateCounts(filtered_views_b, unit)

    // Generate buckets
    const allBuckets = generateAllBuckets(cutoff, now, unit)

    // Calculate cumulative values for each variant
    let cumulativeViewsA = 0
    let cumulativeViewsB = 0
    let cumulativeRevenueA = 0
    let cumulativeRevenueB = 0

    const result = []

    allBuckets.forEach(bucket => {
      const viewsA = agg_views_a[bucket] || 0
      const viewsB = agg_views_b[bucket] || 0
      const revenueA = agg_rev_a[bucket] || 0
      const revenueB = agg_rev_b[bucket] || 0

      cumulativeViewsA += viewsA
      cumulativeViewsB += viewsB
      cumulativeRevenueA += revenueA
      cumulativeRevenueB += revenueB

      // Add data point if either variant has views
      if (cumulativeViewsA > 0 || cumulativeViewsB > 0) {
        result.push({
          views: cumulativeViewsA, // Variant A views on x-axis
          viewsB: cumulativeViewsB, // Store variant B views separately
          revenueA: cumulativeRevenueA,
          revenueB: cumulativeRevenueB,
        })
      }
    })

    return result
  }

  // Filter stats
  const filtered_a = stats_a.filter(v => v.createdAt >= cutoff)
  const filtered_b = stats_b.filter(v => v.createdAt >= cutoff)

  // Aggregate counts
  const agg_a = stat === 'revenues'
    ? aggregateRevenue(filtered_a, unit, 'amount')
    : aggregateCounts(filtered_a, unit)

  const agg_b = stat === 'revenues'
    ? aggregateRevenue(filtered_b, unit, 'amount')
    : aggregateCounts(filtered_b, unit)

  // Generate all buckets
  const allBuckets = generateAllBuckets(cutoff, now, unit)

  // Map into final array with formatted labels
  return allBuckets.map(bucket => ({
    variantA: agg_a[bucket] || 0,
    variantB: agg_b[bucket] || 0,
    label: formatLabel(bucket, unit),
  }))
}
